/**
 * File: sectionMapping.ts
 * Description: Utility functions for mapping architecture sections to display information
 * Author: System
 * Created: 2025-08-10
 * Last Modified: 2025-08-10
 */

import { ArchitectureSection } from '../types/generation';

export interface SectionInfo {
  title: string;
  description: string;
  requiresDiagram: boolean;
  priority: 1 | 2 | 3;
  category: 'business' | 'technical' | 'implementation' | 'governance';
}

export const SECTION_MAPPING: Record<ArchitectureSection, SectionInfo> = {
  [ArchitectureSection.BUSINESS_PURPOSE]: {
    title: 'Business Purpose Documentation',
    description: 'Generate clear business purpose and value proposition sections',
    requiresDiagram: false,
    priority: 1,
    category: 'business',
  },
  [ArchitectureSection.SOLUTION_SUMMARY]: {
    title: 'Solution Summary',
    description: 'Create high-level solution summaries with context and scope',
    requiresDiagram: false,
    priority: 1,
    category: 'business',
  },
  [ArchitectureSection.ADR_SUMMARY]: {
    title: 'Architecture Decision Records Summary',
    description: 'Summary of key architectural decisions with links to detailed ADRs',
    requiresDiagram: false,
    priority: 1,
    category: 'governance',
  },
  [ArchitectureSection.CONCEPTUAL_ARCHITECTURE]: {
    title: 'Conceptual Architecture',
    description: 'Generate high-level conceptual architecture representations and diagrams, following C4 model',
    requiresDiagram: true,
    priority: 1,
    category: 'technical',
  },
  [ArchitectureSection.DATA_ARCHITECTURE]: {
    title: 'Data Architecture',
    description: 'Generate enterprise data model, conceptual data diagrams, and data governance aligned with Shell standards',
    requiresDiagram: true,
    priority: 1,
    category: 'technical',
  },
  [ArchitectureSection.INTEGRATION_ARCHITECTURE]: {
    title: 'Integration Architecture',
    description: 'Define integration patterns, approaches, and interface documentation',
    requiresDiagram: true,
    priority: 1,
    category: 'technical',
  },
  [ArchitectureSection.IMPACTED_COMPONENTS]: {
    title: 'Impacted Components Analysis',
    description: 'Identify and document all components affected by the proposed solution',
    requiresDiagram: false,
    priority: 2,
    category: 'implementation',
  },
  [ArchitectureSection.SECURITY_ARCHITECTURE]: {
    title: 'Security Architecture',
    description: 'Generate security design aligned with Shell standards, BIA ratings, and data classification',
    requiresDiagram: true,
    priority: 1,
    category: 'technical',
  },
  [ArchitectureSection.TECHNOLOGY_ARCHITECTURE]: {
    title: 'Technology Architecture',
    description: 'Create detailed technology stack and infrastructure architecture',
    requiresDiagram: true,
    priority: 1,
    category: 'technical',
  },
  [ArchitectureSection.DEPLOYMENT_MODEL]: {
    title: 'Deployment/Implementation Model',
    description: 'Generate deployment and implementation strategies',
    requiresDiagram: true,
    priority: 2,
    category: 'implementation',
  },
  [ArchitectureSection.RISK_ASSESSMENT]: {
    title: 'Risk & Gaps Analysis',
    description: 'Identify potential risks and architectural gaps with mitigation strategies',
    requiresDiagram: false,
    priority: 2,
    category: 'governance',
  },
  [ArchitectureSection.THREAT_MODELING]: {
    title: 'Threat Modeling',
    description: 'Evidence-based threat modeling using STRIDE, LINDDUN, OWASP-LLM, CAPEC, SecLLM & LLM4Cyber following Shell methodology',
    requiresDiagram: true,
    priority: 1,
    category: 'governance',
  },
};

export const getSectionInfo = (section: ArchitectureSection): SectionInfo => {
  return SECTION_MAPPING[section];
};

export const getSectionsByCategory = (category: SectionInfo['category']): ArchitectureSection[] => {
  return Object.entries(SECTION_MAPPING)
    .filter(([_, info]) => info.category === category)
    .map(([section, _]) => section as ArchitectureSection);
};

export const getSectionsByPriority = (priority: SectionInfo['priority']): ArchitectureSection[] => {
  return Object.entries(SECTION_MAPPING)
    .filter(([_, info]) => info.priority === priority)
    .map(([section, _]) => section as ArchitectureSection);
};

export const getDiagramSections = (): ArchitectureSection[] => {
  return Object.entries(SECTION_MAPPING)
    .filter(([_, info]) => info.requiresDiagram)
    .map(([section, _]) => section as ArchitectureSection);
};
